import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import * as errorHandlerActions from '@/components/errorHandler/Actions';
import {
	getBridgeLockerCheckPasswordPath,
	getBridgeRoomMapPath,
	getBridgeRoomMessagesPath,
	getBridgeTaskFinishPath,
	ROUTES_API,
} from '@/services/Constants';
import { processRequest } from '@/services/Api';
import * as bridgeActions from '../store/actions';
import { bridgeActionTypes } from '../store/constants';

import type {
	CheckLockerPasswordRequestAction,
	FinishTaskRequestAction,
	GetMapRequestAction,
	GetRoomRequestAction,
	GetRoomSuccessAction,
	GetTaskRequestAction,
	SendRoomMessageRequestAction,
} from '../store/actions';
import type { BridgeRoom, BridgeTaskDetail } from '../types';

export default function* bridgeSagas(): Generator<any, void, any> {
	yield all([
		takeLatest(bridgeActionTypes.BRIDGE_GET_EPISODES_REQUEST, handleGetEpisodesRequest),
		takeLatest(bridgeActionTypes.BRIDGE_GET_ROOM_REQUEST, handleGetRoomRequest),
		takeLatest(bridgeActionTypes.BRIDGE_GET_MAP_REQUEST, handleGetMapRequest),
		takeLatest(bridgeActionTypes.BRIDGE_GET_TASK_REQUEST, handleGetTaskRequest),
		takeLatest(bridgeActionTypes.BRIDGE_FINISH_TASK_REQUEST, handleFinishTaskRequest),
		takeLatest(
			bridgeActionTypes.BRIDGE_CHECK_LOCKER_PASSWORD_REQUEST,
			handleCheckLockerPasswordRequest,
		),
		takeLatest(
			bridgeActionTypes.BRIDGE_SEND_ROOM_MESSAGE_REQUEST,
			handleSendRoomMessageRequest,
		),
	]);
}

function* handleGetEpisodesRequest(): Generator<any, void, any> {
	try {
		const response = yield call(processRequest, ROUTES_API.BRIDGE_EPISODES, 'GET');
		const episodes = response.data?.episodes ?? [];
		yield put(bridgeActions.getEpisodesSuccess(episodes));
	} catch (error: any) {
		yield put(bridgeActions.getEpisodesError(error));
	}
}

function* handleGetRoomRequest(action: GetRoomRequestAction): Generator<any, void, any> {
	try {
		const { roomId } = action.payload;
		const endpoint =
			roomId !== undefined
				? `${ROUTES_API.BRIDGE_ROOM}/${roomId}`
				: ROUTES_API.BRIDGE_ROOM_START;

		const { data } = yield call(processRequest, endpoint, 'GET');

		yield put(bridgeActions.getRoomSuccess(data.room));
		yield put(bridgeActions.getEpisodesRequest());
	} catch (error: any) {
		yield put(bridgeActions.getRoomError(error));
	}
}

function* handleGetMapRequest(action: GetMapRequestAction): Generator<any, void, any> {
	try {
		const { roomId } = action.payload;
		const { data } = yield call(processRequest, getBridgeRoomMapPath(roomId), 'GET');
		yield put(bridgeActions.getMapSuccess(data.map ?? data));
	} catch (error: any) {
		const message: string = error?.message ?? String(error);
		yield put(bridgeActions.getMapError(message));
		yield put(errorHandlerActions.handleError(error, 'notification'));
	}
}

function* handleGetTaskRequest(action: GetTaskRequestAction): Generator<any, void, any> {
	try {
		const { roomId, taskType, taskId } = action.payload;
		const endpoint = ROUTES_API.BRIDGE_TASK.replace(':roomId', String(roomId))
			.replace(':taskType', taskType)
			.replace(':id', String(taskId));

		const { data } = yield call(processRequest, endpoint, 'GET');

		yield put(bridgeActions.getTaskSuccess(data.study_go_bridge_room_task));
	} catch (error: any) {
		yield put(bridgeActions.getTaskError(error));
	}
}

function* handleFinishTaskRequest(action: FinishTaskRequestAction): Generator<any, void, any> {
	try {
		const room: BridgeRoom | null = yield select(
			({ bridgeReducer }: any) => bridgeReducer.room,
		);
		const task: BridgeTaskDetail | null = yield select(
			({ bridgeReducer }: any) => bridgeReducer.task,
		);

		if (!room || !task) {
			yield put(bridgeActions.finishTaskError('Missing room or task'));
			return;
		}

		const { gameOutcome, score, answerIds } = action.payload;
		const requestBody = {
			...(gameOutcome && { game_outcome: gameOutcome }),
			...(score !== undefined && { score }),
			...(answerIds?.length && { answer_ids: answerIds }),
		};

		const { data } = yield call(
			processRequest,
			getBridgeTaskFinishPath(room.id, task.type, task.id),
			'POST',
			requestBody,
		);
		const hints = data?.episode_hints ?? [];
		const result = data?.result_modal ?? null;
		yield put(bridgeActions.finishTaskSuccess(hints, result));
		yield put(bridgeActions.getRoomRequest(room.id));
	} catch (error: any) {
		yield put(bridgeActions.finishTaskError(error));
	}
}

function* handleSendRoomMessageRequest(
	action: SendRoomMessageRequestAction,
): Generator<any, void, any> {
	const { roomId, messageType } = action.payload;

	try {
		yield call(processRequest, getBridgeRoomMessagesPath(roomId), 'POST', {
			message_type: messageType,
		});

		if (messageType === 'episode_end') {
			yield put(bridgeActions.getRoomRequest(roomId));
		}
	} catch (error: any) {
		yield put(errorHandlerActions.handleError(error, 'notification'));
	}
}

function* handleCheckLockerPasswordRequest(
	action: CheckLockerPasswordRequestAction,
): Generator<any, void, any> {
	try {
		const { episodeId, password } = action.payload;
		const { data } = yield call(
			processRequest,
			getBridgeLockerCheckPasswordPath(episodeId),
			'PUT',
			{ password },
		);

		if (data?.success) {
			const agendaData = {
				title: data.completed_episode_text?.title ?? '',
				description: data.completed_episode_text?.description ?? '',
				imageUrl: data.agenda_page?.agenda_page_image_desktop?.page_2 ?? '',
			};
			yield put(bridgeActions.checkLockerPasswordSuccess(agendaData));
			yield put(bridgeActions.closeLockerComputerModal());
			yield put(bridgeActions.openLockerAgendaModal());

			const room: BridgeRoom | null = yield select(
				({ bridgeReducer }: any) => bridgeReducer.room,
			);

			if (room) {
				yield put(bridgeActions.getRoomRequest(room.id));
			}
		}
	} catch (error: any) {
		if (error?.response?.status === 422) {
			const apiError = error.response.data?.error ?? 'wrong_password';
			yield put(bridgeActions.checkLockerPasswordWrong(apiError));
		} else {
			yield put(bridgeActions.checkLockerPasswordError(error));
		}
	}
}
